import { useCallback, useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgTrash } from "react-icons/cg";
import { Modal } from "react-bootstrap";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const IST_OFFSET_MS = 330 * 60 * 1000;

// "Today" is always evaluated in IST (Asia/Kolkata), like the backend
const todayIST = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);

const isValidDate = (value) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value || "") &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) &&
  new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

const formatDate = (value) => {
  if (!isValidDate(value)) return value || "-";
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const EMPTY_FORM = { date: "", reason: "" };

const validateHolidayForm = (form) => {
  const errors = {};
  const today = todayIST();
  if (!isValidDate(form.date)) {
    errors.date = "Please select a date";
  } else if (form.date < today) {
    errors.date = "Past dates are not allowed";
  }
  return errors;
};

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [upcomingOnly, setUpcomingOnly] = useState(true);

  // search + pagination
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);

  // add modal
  const [addModel, setAddModel] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [check, setCheck] = useState(null); // result of /admin/holidays/check for the current form
  const [addError, setAddError] = useState("");
  const [addSkipped, setAddSkipped] = useState([]);

  // delete modals
  const [deleteId, setDeleteId] = useState(null);
  const [bulkDeleteModel, setBulkDeleteModel] = useState(false);

  const notify = (message, type = "success") => setSnack({ notification: { message, type } });

  const getHolidays = useCallback(async (upcoming) => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/holidays${upcoming ? "?upcoming=true" : ""}`,
        { method: "GET" }
      );
      const data = response.data || {};
      if (data.s === 200) {
        const list = Array.isArray(data.r) ? data.r : [];
        setHolidays([...list].sort((a, b) => String(a.date).localeCompare(String(b.date))));
      } else {
        setHolidays([]);
        if (data.m) setSnack({ notification: { message: data.m, type: "error" } });
      }
    } catch (err) {
      console.error("Failed to fetch holidays:", err);
      setHolidays([]);
      setSnack({ notification: { message: "Error fetching holidays", type: "error" } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setPageNo(1);
    getHolidays(upcomingOnly);
  }, [upcomingOnly, getHolidays]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const q = query.trim().toLowerCase();
  const filteredHolidays = q
    ? holidays.filter((h) =>
        [h.date, formatDate(h.date), h.reason].some((field) =>
          String(field ?? "").toLowerCase().includes(q)
        )
      )
    : holidays;

  const startIndex = (pageNo - 1) * limit;
  const paginatedHolidays = filteredHolidays.slice(startIndex, startIndex + limit);
  const totalPages = Math.ceil(filteredHolidays.length / limit) || 1;

  const visibleIds = paginatedHolidays.map((h) => h._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  // ---- delete ----
  const handleDelete = async () => {
    const id = deleteId;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/holidays/${id}`, { method: "DELETE" });
      const data = response.data || {};
      if (data.s === 200) {
        setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
        notify("Holiday deleted successfully");
        getHolidays(upcomingOnly);
      } else {
        notify(data.m || "Error deleting holiday", "error");
      }
    } catch (err) {
      console.error("Error deleting holiday:", err);
      notify("Error deleting holiday", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const openBulkDelete = () => {
    if (!selectedIds.length) {
      notify("Select at least one holiday", "error");
      return;
    }
    setBulkDeleteModel(true);
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/holidays`, {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      const data = response.data || {};
      if (data.s === 200) {
        const deleted = data.r?.deleted ?? ids.length;
        notify(`${plural(deleted, "holiday", "holidays")} deleted successfully`);
        setSelectedIds([]);
        getHolidays(upcomingOnly);
      } else {
        notify(data.m || "Error deleting holidays", "error");
      }
    } catch (err) {
      console.error("Error deleting holidays:", err);
      notify("Error deleting holidays", "error");
    } finally {
      setLoading(false);
      setBulkDeleteModel(false);
    }
  };

  // ---- add ----
  const formErrors = validateHolidayForm(form);
  const isFormValid = Object.keys(formErrors).length === 0;
  const today = todayIST();

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setTouched({});
    setCheck(null);
    setAddError("");
    setAddSkipped([]);
    setAddModel(true);
  };

  const closeAddModal = () => {
    setAddModel(false);
    setCheck(null);
    setAddError("");
    setAddSkipped([]);
  };

  // any change to the date invalidates the previous order check / confirmation
  const handleFieldChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field !== "reason") setCheck(null);
    setAddError("");
    setAddSkipped([]);
  };

  const buildPayload = () => {
    const payload = { date: form.date };
    if (form.reason.trim()) payload.reason = form.reason.trim();
    return payload;
  };

  const runCheck = async () => {
    const params = new URLSearchParams({ date: form.date });
    const response = await AuthInterceptor(`/admin/holidays/check?${params.toString()}`, {
      method: "GET",
    });
    const data = response.data || {};
    if (data.s !== 200) throw new Error(data.m || "Could not check existing orders");
    return {
      dates: Array.isArray(data.r?.dates) ? data.r.dates : [],
      totalOrders: Number(data.r?.totalOrders) || 0,
    };
  };

  const saveHoliday = async () => {
    const response = await AuthInterceptor(`/admin/holidays`, {
      method: "POST",
      body: JSON.stringify(buildPayload()),
    });
    const data = response.data || {};
    const created = Array.isArray(data.r?.created) ? data.r.created : [];
    const skipped = Array.isArray(data.r?.skipped) ? data.r.skipped : [];

    if (data.s === 201 || data.s === 200) {
      const createdMsg = `${plural(created.length, "holiday", "holidays")} added`;
      notify(
        skipped.length ? `${createdMsg}, ${skipped.length} skipped (already holidays)` : data.m || createdMsg,
        skipped.length ? "alert" : "success"
      );
      closeAddModal();
      getHolidays(upcomingOnly);
    } else {
      setAddSkipped(skipped);
      setAddError(data.m || "Failed to add holiday");
    }
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    setTouched({ date: true });
    if (!isFormValid) return;

    try {
      setLoading(true);
      setAddError("");
      setAddSkipped([]);

      // first submit: check for existing orders on this date
      if (!check) {
        const result = await runCheck();
        if (result.totalOrders > 0) {
          // show the warning and require an explicit "Add anyway"
          setCheck(result);
          return;
        }
      }
      await saveHoliday();
    } catch (err) {
      console.error("Error adding holiday:", err);
      setAddError(err.message || "Error adding holiday");
    } finally {
      setLoading(false);
    }
  };

  const renderDateField = (field, label, props = {}, hint = null) => {
    const showError = touched[field] && formErrors[field];
    return (
      <div className="mb-3">
        <label className="form-label" htmlFor={`holiday-${field}`}>
          {label}
        </label>
        <input
          id={`holiday-${field}`}
          type="date"
          className={`form-control ${showError ? "is-invalid" : ""}`}
          value={form[field]}
          onChange={handleFieldChange(field)}
          onBlur={() => setTouched((prev) => ({ ...prev, [field]: true }))}
          disabled={loading}
          {...props}
        />
        {showError ? (
          <div className="invalid-feedback">{formErrors[field]}</div>
        ) : (
          hint && <div className="form-text">{hint}</div>
        )}
      </div>
    );
  };

  const alreadyHolidayDates = (check?.dates || []).filter((d) => d.alreadyHoliday);

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Holidays"
          searchValue={query}
          searchPlaceholder="Search by date or reason..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        >
          <div className="form-check form-switch mb-0">
            <input
              id="holidays-upcoming-only"
              type="checkbox"
              className="form-check-input"
              checked={upcomingOnly}
              onChange={(e) => setUpcomingOnly(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="holidays-upcoming-only">
              Upcoming only
            </label>
          </div>
        </ListHeader>

        {/* Bulk actions | Add */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {paginatedHolidays.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-danger" onClick={openBulkDelete}>
                Delete
              </button>
            </>
          )}
          <button className="btn btn-sm btn-outline-custom ms-auto" onClick={openAddModal}>
            Add Holiday
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover gap-2 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all holidays on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Date</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedHolidays.map((h, idx) => {
                  return (
                    <tr key={h._id || idx}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.includes(h._id)}
                          onChange={() => toggleRowSelection(h._id)}
                          aria-label={`Select holiday ${h.date}`}
                        />
                      </td>
                      <td>{startIndex + idx + 1}</td>
                      <td className="text-nowrap">
                        {formatDate(h.date)}
                        {h.date < today && <span className="badge bg-light text-muted ms-2">Past</span>}
                      </td>
                      <td>{h.reason || "-"}</td>
                      <td className="text-nowrap">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setDeleteId(h._id)}
                        >
                          <CgTrash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}

              {!loading && paginatedHolidays.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    {q
                      ? "No holidays match your search"
                      : upcomingOnly
                        ? "No upcoming holidays"
                        : "No holidays found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={filteredHolidays.length > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      {/* Add Holiday Modal */}
      <Modal show={addModel} onHide={closeAddModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Holiday</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddHoliday} noValidate>
            {renderDateField("date", "Date", { min: today })}
            <div className="mb-3">
              <label className="form-label" htmlFor="holiday-reason">
                Reason (optional)
              </label>
              <input
                id="holiday-reason"
                type="text"
                className="form-control"
                placeholder="e.g. Diwali"
                value={form.reason}
                onChange={handleFieldChange("reason")}
                disabled={loading}
                maxLength={200}
              />
            </div>
            <div className="form-text mb-3">
              No slots are shown to users on holidays (applies to all service locations).
            </div>

            {check && check.totalOrders > 0 && (
              <div className="alert alert-warning py-2 small">
                <div className="fw-semibold">
                  {plural(check.totalOrders, "order is", "orders are")} already booked on{" "}
                  {formatDate(form.date)}. They will NOT be cancelled automatically.
                </div>
              </div>
            )}
            {alreadyHolidayDates.length > 0 && (
              <div className="alert alert-info py-2 small">
                {formatDate(form.date)} is already a holiday.
              </div>
            )}

            {addError && (
              <div className="alert alert-danger py-2 small">
                <div className="fw-semibold">{addError}</div>
                {addSkipped.length > 0 && (
                  <ul className="mb-0 mt-1 ps-3">
                    {addSkipped.map((s, i) => (
                      <li key={`${s.date}-${i}`}>
                        {formatDate(s.date)}: {s.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button
              type="submit"
              className={`btn ${check && check.totalOrders > 0 ? "btn-warning" : "btn-primary"}`}
              disabled={loading || (Object.keys(touched).length > 0 && !isFormValid)}
            >
              {loading ? (
                <Spinnerr show={loading} />
              ) : check && check.totalOrders > 0 ? (
                "Add anyway"
              ) : (
                "Add Holiday"
              )}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={!!deleteId}
        count={1}
        singular="holiday"
        plural="holidays"
        loading={loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
      <DeleteConfirmModal
        show={bulkDeleteModel}
        count={selectedIds.length}
        singular="holiday"
        plural="holidays"
        loading={loading}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteModel(false)}
      />
    </>
  );
};

export default Holidays;
